import { useState, useEffect } from 'react';
import API from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { ShoppingBag, TrendingUp, Calendar, RefreshCw, Package, Crown } from 'lucide-react';

const STATUS_COLORS = {
  Erstattet: '#4ade80', Reviewed: '#a855f7', Eingereicht: '#3b82f6',
  Bestellt: '#f97316', Privat: '#6b7280', Storno: '#ef4444',
};
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WEEKDAYS_SORTED = [1,2,3,4,5,6,0];

const fmt = (v) => v ? parseFloat(v).toFixed(2).replace('.', ',') + ' €' : '0,00 €';
const fmtShort = (v) => { const n=parseFloat(v)||0; return n>=1000?(n/1000).toFixed(1).replace('.',',')+'k':n.toFixed(0); };
const pct = (v) => `${(parseFloat(v)||0).toFixed(1).replace('.', ',')} %`;
const clamp = (n, min=0, max=100) => Math.min(Math.max(n, min), max);
const EUGEN_TARGET_COMMISSION = 17000; // Monatsziel auf Basis Abrechnungsbetrag / betrag
const EUGEN_MAX_PAYOUT = 7000;

function StatCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="card" style={{ padding:'18px 20px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div style={{ flex:1 }}>
          <div style={{ color:'var(--text3)', fontSize:10, textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:'Syne', fontWeight:700, marginBottom:6 }}>{label}</div>
          <div style={{ fontSize:28, fontFamily:'Syne', fontWeight:800, color:color||'var(--text)', lineHeight:1 }}>{value}</div>
          {sub && <div style={{ color:'var(--text3)', fontSize:11, marginTop:4 }}>{sub}</div>}
        </div>
        {Icon && <div style={{ background:'rgba(124,106,247,0.12)', borderRadius:10, padding:8 }}><Icon size={18} color="var(--accent)" /></div>}
      </div>
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom:12 }}>
      <h3 style={{ fontFamily:'Syne', fontSize:14, fontWeight:800 }}>{children}</h3>
      {sub && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: active ? 'var(--accent)' : 'transparent',
      color: active ? '#fff' : 'var(--text3)',
      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius:6, padding:'3px 10px', fontSize:10, cursor:'pointer', fontFamily:'Syne', fontWeight:700
    }}>{children}</button>
  );
}

const EurTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4, fontFamily:'Syne', fontWeight:700 }}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{ color:p.color||'var(--accent2)' }}>{fmt(p.value)}</div>)}
    </div>
  );
};
const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4, fontFamily:'Syne', fontWeight:700 }}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{ color:p.color||'var(--accent2)' }}>{p.value} Bestellungen</div>)}
    </div>
  );
};
const AvgTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4, fontFamily:'Syne', fontWeight:700 }}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{ color:p.color||'var(--accent)' }}>Ø {fmt(p.value)} / Tag</div>)}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [oldStats, setOldStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentView, setAgentView] = useState('month');
  const [chartView, setChartView] = useState('monthly');
  const [mainChartMetric, setMainChartMetric] = useState('refunded');
  const [accountValueMetric, setAccountValueMetric] = useState('commission');
  const [eugenMonth, setEugenMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [commissionStats, setCommissionStats] = useState(null);
  const [warenwertStatus, setWarenwertStatus] = useState('vine');
  const [warenwertMonth, setWarenwertMonth] = useState('');
  const [warenwertData, setWarenwertData] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const lastVisit = localStorage.getItem('lastVisit');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, oldRes, commissionRes] = await Promise.all([
        API.get('/orders/dashboard-stats'),
        API.get('/orders/stats'),
        API.get('/orders/commission-dashboard-stats').catch(() => ({ data: null })),
      ]);
      setStats(dashRes.data);
      setOldStats(oldRes.data);
      setCommissionStats(commissionRes.data);
    } catch(err) { console.error(err); }
    finally { setLoading(false); localStorage.setItem('lastVisit', new Date().toISOString()); }
  };

  const fetchWarenwert = async (statusGroup, month) => {
    try {
      const params = { statusGroup };
      if (month) params.month = month;
      const res = await API.get('/orders/warenwert-accounts', { params });
      setWarenwertData(res.data.accounts || []);
      setSelectedAccounts([]);
    } catch(err) { console.error(err); }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchWarenwert(warenwertStatus, warenwertMonth); }, [warenwertStatus, warenwertMonth]);

  if (loading) return <div className="page" style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh' }}><p style={{ color:'var(--text3)' }}>Laden...</p></div>;

  const agentData = agentView === 'month' ? stats?.agentRankingMonth : stats?.agentRankingYear;

  const monthData = (stats?.monthlyTotals || []).map(m => {
    const [y, mo] = m.month.split('-');
    return { ...m, label:`${mo}/${y.slice(2)}`, total:parseFloat(m.total) };
  });

  const yearData = (stats?.yearlyTotals || []).map(y => ({
    ...y, label:y.year, total:parseFloat(y.total)
  }));

  const commissionMonthData = (commissionStats?.monthlyCommission || []).map(m => {
    const [y, mo] = m.month.split('-');
    return { ...m, label:`${mo}/${y.slice(2)}`, total:parseFloat(m.total) };
  });

  const commissionYearData = (commissionStats?.yearlyCommission || []).map(y => ({
    ...y, label:y.year, total:parseFloat(y.total)
  }));

  const refundedChartData = chartView === 'monthly' ? monthData : yearData;
  const commissionChartData = chartView === 'monthly' ? commissionMonthData : commissionYearData;
  const chartData = mainChartMetric === 'commission' ? commissionChartData : refundedChartData;

  const avgData = (stats?.avgPerMonth || []).map(m => {
    const [y, mo] = m.month.split('-');
    return { label:`${mo}/${y.slice(2)}`, avg:parseFloat(m.avg) };
  }).slice(-12);

  const pieData = (stats?.byStatus || []).map(s => ({ name:s.status, value:parseInt(s.count), total:parseFloat(s.total)||0 }));
  const reqData = (stats?.reqTypeStats || [])
    .filter(r => ['Text','Picture','Video'].includes(r.req_type))
    .map(r => ({ name:r.req_type||'?', value:parseInt(r.count) }));

  const generateAvailableMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      months.push(`${yyyy}-${mm}`);
    }
    return months;
  };
  const availableMonths = generateAvailableMonths();
  const eugenAvailableMonths = (commissionStats?.eugenMonths?.length
    ? commissionStats.eugenMonths.map((m) => m.month)
    : availableMonths);

  const wdMap = {};
  (stats?.weekdayStats || []).forEach(w => { wdMap[w.dow] = parseInt(w.count); });
  const weekdayData = WEEKDAYS_SORTED.map(d => ({ label:WEEKDAYS[d], count:wdMap[d]||0 }));

  const accountData = stats?.accountRanking || [];
  const eugenMonthStats = (commissionStats?.eugenMonths || []).find((m) => m.month === eugenMonth);
  const eugen = eugenMonthStats || {
    month: eugenMonth,
    total_commission: 0,
    avg_commission: 0,
    order_count: 0,
    statuses: [],
    accounts: []
  };
  const eugenCommission = parseFloat(eugen.total_commission || 0);
  const eugenProgress = clamp((eugenCommission / EUGEN_TARGET_COMMISSION) * 100);
  const eugenPayout = Math.min((eugenCommission / EUGEN_TARGET_COMMISSION) * EUGEN_MAX_PAYOUT, EUGEN_MAX_PAYOUT);
  const eugenStatusTotal = (eugen.statuses || []).reduce((sum, s) => sum + parseInt(s.count || 0, 10), 0);
  const eugenMonthLabel = eugenMonth ? `${eugenMonth.split('-')[1]}/${eugenMonth.split('-')[0].slice(2)}` : 'Monat';
  const accountAnalysisData = accountValueMetric === 'commission'
    ? (warenwertMonth
        ? (commissionStats?.commissionAccountsByMonth || []).filter((a) => a.month === warenwertMonth)
        : (commissionStats?.commissionAccounts || [])
      ).slice().sort((a, b) => parseFloat(b.commission || 0) - parseFloat(a.commission || 0))
    : warenwertData;
  const accountValueKey = accountValueMetric === 'commission' ? 'commission' : 'warenwert';

  const toggleAccount = (accountName) => {
    setSelectedAccounts(prev => 
      prev.includes(accountName) 
        ? prev.filter(a => a !== accountName)
        : [...prev, accountName]
    );
  };

  const selectedSum = accountAnalysisData
    .filter(a => selectedAccounts.includes(a.account_name))
    .reduce((sum, a) => sum + parseFloat(a[accountValueKey] || 0), 0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p style={{ color:'var(--text2)', marginTop:4, fontSize:13 }}>{lastVisit ? `Letzter Besuch: ${new Date(lastVisit).toLocaleString('de-DE')}` : 'Willkommen!'}</p>
        </div>
        <button className="btn btn-ghost" onClick={fetchAll}><RefreshCw size={14} />Aktualisieren</button>
      </div>

      {/* Zeitraum Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <StatCard label="Diese Woche" value={stats?.week?.count||0} sub={fmt(stats?.week?.total)} icon={Calendar} />
        <StatCard label="Dieser Monat" value={stats?.month?.count||0} sub={fmt(stats?.month?.total)} icon={ShoppingBag} />
        <StatCard label="Dieses Jahr" value={stats?.year?.count||0} sub={fmt(stats?.year?.total)} icon={TrendingUp} />
        <StatCard label="Gesamt" value={oldStats?.total?.count||0} sub={fmt(oldStats?.total?.total)} icon={Package} />
      </div>

      {/* Balken Erstattungen / Abrechnungsbetrag + Pie */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <SectionTitle sub={mainChartMetric==='commission' ? 'Abrechnungsbetrag nach Bestelldatum' : (chartView==='monthly' ? 'Erstattungen pro Monat — alle Zeit' : 'Erstattungen pro Jahr')}>💰 Monatsanalyse</SectionTitle>
            <div style={{ display:'flex', gap:4, flexWrap:'wrap', justifyContent:'flex-end' }}>
              <TabBtn active={mainChartMetric==='refunded'} onClick={() => setMainChartMetric('refunded')}>Erstattet</TabBtn>
              <TabBtn active={mainChartMetric==='commission'} onClick={() => setMainChartMetric('commission')}>Abrechnungsbetrag</TabBtn>
              <TabBtn active={chartView==='monthly'} onClick={() => setChartView('monthly')}>Monatlich</TabBtn>
              <TabBtn active={chartView==='yearly'} onClick={() => setChartView('yearly')}>Jährlich</TabBtn>
            </div>
          </div>
          {chartData.length === 0 ? <div style={{ color:'var(--text3)',fontSize:13,textAlign:'center',padding:30 }}>Keine Daten</div> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top:4, right:4, left:-10, bottom:0 }}>
                <XAxis dataKey="label" tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                <Tooltip content={<EurTooltip />} />
                <Bar dataKey="total" fill="var(--accent2)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="card">
          <SectionTitle sub="Dieses Jahr">🥧 Status Verteilung</SectionTitle>
          {pieData.length === 0 ? <div style={{ color:'var(--text3)',fontSize:13,textAlign:'center',padding:30 }}>Keine Daten</div> : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                  {pieData.map((e,i) => <Cell key={i} fill={STATUS_COLORS[e.name]||'#6b7280'} />)}
                </Pie>
                <Tooltip formatter={(v,n,p) => [`${v} (${fmt(p.payload.total)})`, p.payload.name]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Ø Betrag + Wochentag */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div className="card">
          <SectionTitle sub="Letzte 12 Monate — Summe Betrag ÷ aktive Tage">📈 Ø Tagesumsatz</SectionTitle>
          {avgData.length === 0 ? <div style={{ color:'var(--text3)',fontSize:13,textAlign:'center',padding:20 }}>Keine Daten</div> : (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={avgData} margin={{ top:4, right:4, left:-10, bottom:0 }}>
                <XAxis dataKey="label" tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                <Tooltip content={<AvgTooltip />} />
                <Line type="monotone" dataKey="avg" stroke="var(--accent)" strokeWidth={2} dot={{ fill:'var(--accent)', r:3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <SectionTitle sub="Dieses Jahr — an welchem Tag kommen Bestellungen">📅 Wochentag-Analyse</SectionTitle>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={weekdayData} margin={{ top:4, right:4, left:-10, bottom:0 }}>
              <XAxis dataKey="label" tick={{ fontSize:11, fill:'var(--text3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CountTooltip />} />
              <Bar dataKey="count" radius={[4,4,0,0]}>
                {weekdayData.map((e,i) => {
                  const max = Math.max(...weekdayData.map(d => d.count));
                  return <Cell key={i} fill={e.count === max ? 'var(--accent)' : 'var(--accent2)'} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Agent Ranking (BLEIBT GROẞ) */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <SectionTitle sub={agentView==='month'?'Dieser Monat':'Dieses Jahr'}>🏆 Agent Ranking</SectionTitle>
          <div style={{ display:'flex', gap:4 }}>
            <TabBtn active={agentView==='month'} onClick={() => setAgentView('month')}>Monat</TabBtn>
            <TabBtn active={agentView==='year'} onClick={() => setAgentView('year')}>Jahr</TabBtn>
          </div>
        </div>
        {!agentData?.length ? <div style={{ color:'var(--text3)',fontSize:13,textAlign:'center',padding:20 }}>Keine Daten</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {agentData.map((a,i) => {
              const pct = (parseFloat(a.count)/parseFloat(agentData[0].count))*100;
              const medals = ['🥇','🥈','🥉'];
              return (
                <div key={a.agent_name} style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:24, fontSize:14, textAlign:'center' }}>{medals[i]||`${i+1}.`}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                      <span style={{ fontSize:12, color:'var(--text)', fontWeight:600 }}>{a.agent_name}</span>
                      <span style={{ fontSize:11, color:'var(--text3)' }}>{a.count} · {fmt(a.total)}</span>
                    </div>
                    <div style={{ height:4, background:'var(--bg3)', borderRadius:2 }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:i===0?'#fbbf24':i===1?'#9ca3af':i===2?'#cd7c2e':'var(--accent)', borderRadius:2 }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* NEUES LAYOUT: Abrechnungsbetrag/Warenwert GROẞ + Account Ranking + Auftragstypen klein */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
        
        {/* COMMISSION / WARENWERT MIT MULTI-SELECT (GROẞ) */}
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <SectionTitle sub={accountValueMetric==='commission' ? 'Standard: Abrechnungsbetrag je Account' : 'Nebeninfo: Warenwert je Account'}>{accountValueMetric==='commission' ? '💳 Abrechnungsbetrag / Account' : '🛒 Warenwert / Account'}</SectionTitle>
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            <TabBtn active={accountValueMetric==='commission'} onClick={() => { setAccountValueMetric('commission'); setSelectedAccounts([]); }}>Abrechnungsbetrag</TabBtn>
            <TabBtn active={accountValueMetric==='warenwert'} onClick={() => { setAccountValueMetric('warenwert'); setSelectedAccounts([]); }}>Warenwert</TabBtn>
            {accountValueMetric === 'warenwert' && <TabBtn active={warenwertStatus==='vine'} onClick={() => setWarenwertStatus('vine')}>Vine</TabBtn>}
            {accountValueMetric === 'warenwert' && <TabBtn active={warenwertStatus==='privat'} onClick={() => setWarenwertStatus('privat')}>Privat</TabBtn>}
            <select value={warenwertMonth} onChange={e => setWarenwertMonth(e.target.value)} style={{
              background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6,
              color:'var(--text2)', fontSize:11, padding:'4px 10px', cursor:'pointer', fontFamily:'Syne'
            }}>
              <option value=''>Alle Monate</option>
              {availableMonths.map(m => {
                const [y, mo] = m.split('-');
                return <option key={m} value={m}>{mo}/{y.slice(2)}</option>;
              })}
            </select>
          </div>
          
          {!accountAnalysisData.length ? <div style={{ color:'var(--text3)', fontSize:12, textAlign:'center', padding:20 }}>Keine Daten</div> : (
            <>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {accountAnalysisData.map((a,i) => {
                  const max = parseFloat(accountAnalysisData[0]?.[accountValueKey] || 0) || 1;
                  const pct = (parseFloat(a[accountValueKey] || 0)/max)*100;
                  const isSelected = selectedAccounts.includes(a.account_name);
                  return (
                    <div 
                      key={a.account_name}
                      onClick={() => toggleAccount(a.account_name)}
                      style={{
                        cursor: 'pointer',
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: isSelected ? 'var(--bg3)' : 'transparent',
                        border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ 
                            cursor:'pointer',
                            width: 16,
                            height: 16
                          }}
                        />
                        <span style={{ 
                          fontSize:13, 
                          color:'var(--text)', 
                          fontWeight:600,
                          fontFamily:'Syne',
                          flex: 1
                        }}>
                          {a.account_name}
                        </span>
                        <span style={{ 
                          fontSize:14, 
                          color:'var(--accent2)', 
                          fontFamily:'DM Mono',
                          fontWeight: 600
                        }}>
                          {fmt(a[accountValueKey])}
                        </span>
                      </div>
                      <div style={{ height:6, background:'var(--bg3)', borderRadius:3 }}>
                        <div style={{ 
                          width:`${pct}%`, 
                          height:'100%', 
                          background: isSelected 
                            ? 'var(--accent)' 
                            : (warenwertStatus==='privat'?'#6b7280':'var(--accent2)'), 
                          borderRadius:3,
                          transition: 'all 0.2s'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {selectedAccounts.length > 0 && (
                <div style={{
                  borderTop: '2px solid var(--accent)',
                  paddingTop: 16,
                  marginTop: 16,
                  background: 'var(--bg3)',
                  padding: 16,
                  borderRadius: 8
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ 
                      fontSize:12, 
                      color:'var(--text2)', 
                      fontFamily:'Syne', 
                      fontWeight:700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      SUMME ({selectedAccounts.length} {selectedAccounts.length === 1 ? 'Account' : 'Accounts'})
                    </span>
                    <span style={{ 
                      fontSize:20, 
                      color:'var(--accent)', 
                      fontFamily:'DM Mono', 
                      fontWeight:800
                    }}>
                      {fmt(selectedSum)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* RECHTE SPALTE: Account Ranking + Auftragstypen (KLEIN, STACK) */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          
          {/* Account Ranking */}
          <div className="card">
            <SectionTitle sub="Dieses Jahr">👤 Account Ranking</SectionTitle>
            {!accountData.length ? <div style={{ color:'var(--text3)',fontSize:12 }}>Keine Daten</div> : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {accountData.slice(0, 5).map((a,i) => {
                  const pct = (parseFloat(a.count)/parseFloat(accountData[0].count))*100;
                  return (
                    <div key={a.account_name}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                        <span style={{ fontSize:11, color:'var(--text2)', fontWeight:600 }}>{a.account_name}</span>
                        <span style={{ fontSize:11, color:'var(--text3)' }}>{a.count}</span>
                      </div>
                      <div style={{ height:3, background:'var(--bg3)', borderRadius:2 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:'var(--accent2)', borderRadius:2 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Auftragstypen */}
          <div className="card">
            <SectionTitle sub="Dieses Jahr">📋 Auftragstypen</SectionTitle>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {reqData.map(r => {
                const total = reqData.reduce((s,x) => s+x.value, 0);
                const pct = total > 0 ? ((r.value/total)*100).toFixed(0) : 0;
                const colors = { Text:'var(--accent)', Picture:'var(--accent2)', Video:'#a855f7' };
                return (
                  <div key={r.name}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                      <span style={{ fontSize:11, color:'var(--text2)' }}>{r.name}</span>
                      <span style={{ fontSize:10, color:'var(--text3)' }}>{r.value} ({pct}%)</span>
                    </div>
                    <div style={{ height:4, background:'var(--bg3)', borderRadius:2 }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:colors[r.name]||'var(--accent)', borderRadius:2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {/* Status Kacheln */}
      <div className="card">
        <SectionTitle sub="Dieses Jahr">📊 Übersicht nach Status</SectionTitle>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {(stats?.byStatus||[]).map(s => (
            <div key={s.status} style={{
              background:'var(--bg3)',
              borderLeft:`3px solid ${STATUS_COLORS[s.status]||'var(--border)'}`,
              borderRadius:8, padding:'10px 16px', minWidth:110,
            }}>
              <div style={{ fontSize:22, fontFamily:'Syne', fontWeight:800, color:STATUS_COLORS[s.status]||'var(--text)' }}>{s.count}</div>
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{s.status}</div>
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:1, fontFamily:'DM Mono' }}>{fmt(s.total)}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Eugen Analyse unten */}
      {commissionStats?.eugen && (
        <div className="card" style={{ marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:14 }}>
            <div>
              <SectionTitle sub={`Accounts 2, 3 und 11 · Monatsziel ${eugenMonthLabel} · Basis ist Abrechnungsbetrag (betrag), nicht die 25€-Provision und nicht Warenwert`}>👑 Eugen Monatsstatus</SectionTitle>
              <div style={{ color:'var(--text3)', fontSize:11 }}>
                Monatliche Auszahlung ist gedeckelt: max. {fmt(EUGEN_MAX_PAYOUT)} bei {fmt(EUGEN_TARGET_COMMISSION)} Abrechnungsbetrag pro Monat.
              </div>
            </div>
            <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
              <select value={eugenMonth} onChange={e => setEugenMonth(e.target.value)} style={{
                background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6,
                color:'var(--text2)', fontSize:11, padding:'5px 10px', cursor:'pointer', fontFamily:'Syne'
              }}>
                {eugenAvailableMonths.map(m => {
                  const [y, mo] = m.split('-');
                  return <option key={m} value={m}>{mo}/{y.slice(2)}</option>;
                })}
              </select>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:'var(--text3)', fontSize:10, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:'Syne', fontWeight:700 }}>Auszahlung {eugenMonthLabel}</div>
                <div style={{ color:'var(--accent)', fontSize:26, fontFamily:'Syne', fontWeight:800 }}>{fmt(eugenPayout)}</div>
              </div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:18 }}>
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                <span style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>Abrechnungsbetrag erreicht</span>
                <span style={{ fontSize:13, color:'var(--text2)', fontFamily:'DM Mono' }}>{fmt(eugenCommission)} / {fmt(EUGEN_TARGET_COMMISSION)}</span>
              </div>
              <div style={{ height:14, background:'var(--bg3)', borderRadius:999, overflow:'hidden', border:'1px solid var(--border)' }}>
                <div style={{ width:`${eugenProgress}%`, height:'100%', background:'var(--accent)', borderRadius:999 }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginTop:14 }}>
                <StatCard label="Bestellungen" value={eugen.order_count || 0} sub={`im Monat ${eugenMonthLabel}`} icon={Crown} />
                <StatCard label="Ø Betrag" value={fmt(eugen.avg_commission)} sub="pro Bestellung" isText />
                <StatCard label="Progress" value={pct(eugenProgress)} sub={eugenCommission > EUGEN_TARGET_COMMISSION ? 'Monatsziel überschritten, Auszahlung gedeckelt' : 'Monatsziel 17k'} color="var(--accent)" />
              </div>
            </div>

            <div>
              <div style={{ fontSize:11, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:'Syne', fontWeight:700, marginBottom:8 }}>
                Status seiner Bestellungen
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {(eugen.statuses || []).map(s => {
                  const statusPct = eugenStatusTotal ? (parseInt(s.count || 0, 10) / eugenStatusTotal) * 100 : 0;
                  return (
                    <div key={s.status}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                        <span style={{ fontSize:11, color:'var(--text2)', fontWeight:600 }}>{s.status}</span>
                        <span style={{ fontSize:11, color:'var(--text3)' }}>{s.count} · {pct(statusPct)}</span>
                      </div>
                      <div style={{ height:5, background:'var(--bg3)', borderRadius:3 }}>
                        <div style={{ width:`${statusPct}%`, height:'100%', background:STATUS_COLORS[s.status]||'var(--accent)', borderRadius:3 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {!!eugen.accounts?.length && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginTop:16 }}>
                {eugen.accounts.map(a => (
                  <div key={a.account_name} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:12 }}>
                    <div style={{ fontFamily:'Syne', fontWeight:800, fontSize:13, marginBottom:4 }}>{a.account_name}</div>
                    <div style={{ color:'var(--accent2)', fontFamily:'DM Mono', fontWeight:700 }}>{fmt(a.commission)}</div>
                    <div style={{ color:'var(--text3)', fontSize:11, marginTop:2 }}>{a.count} Bestellungen</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop:14, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:14 }}>
                <div style={{ fontFamily:'Syne', fontWeight:800, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text2)', marginBottom:10 }}>
                  Abrechnungsgrundlage für Eugen
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {eugen.accounts.map(a => (
                    <div key={`${a.account_name}-summary`} style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                      <span style={{ color:'var(--text2)' }}>{a.account_name}: {a.count} Bestellungen</span>
                      <span style={{ color:'var(--text)', fontFamily:'DM Mono', fontWeight:700 }}>{fmt(a.commission)}</span>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop:'1px solid var(--border)', marginTop:10, paddingTop:10, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                  <div>
                    <div style={{ color:'var(--text3)', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Kumuliert</div>
                    <div style={{ color:'var(--accent2)', fontFamily:'DM Mono', fontSize:16, fontWeight:800 }}>{fmt(eugenCommission)}</div>
                  </div>
                  <div>
                    <div style={{ color:'var(--text3)', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Auszahlung laut Regel</div>
                    <div style={{ color:'var(--accent)', fontFamily:'DM Mono', fontSize:16, fontWeight:800 }}>{fmt(eugenPayout)}</div>
                  </div>
                  <div>
                    <div style={{ color:'var(--text3)', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Deckelung</div>
                    <div style={{ color:'var(--text)', fontFamily:'DM Mono', fontSize:16, fontWeight:800 }}>max. {fmt(EUGEN_MAX_PAYOUT)}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}


    </div>
  );
}
